import { CachePolicies, useFetch } from 'use-http';
import { routes } from '@/webapi/routes';

export function useBillingApi() {
  const { post, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: true,
    },
  }));

  const changePricingTier = async (
    alias: string,
    newTier: string,
  ): Promise<PricingInfoResponse> =>
    post(routes.changePricingTier, { alias, tier: newTier });

  const getBillingInfo = async (alias: string): Promise<PricingInfoResponse> =>
    post(routes.getPricingInfo, { value: alias });

  const upgradeToNewBilling = async (): Promise<PricingInfoResponse> =>
    post(routes.upgradeToNewBilling, {});

  const listTiers = async (
    orgSize: number,
    ordersCount: number,
    enrichMissing = false,
  ): Promise<PublicBillingTierResp> => {
    const response = (await post(routes.listBillingTiers, {
      ordersCount,
      organizationSize: orgSize,
      enrichMissing,
    })) as PublicBillingTierResp;

    if (response?.tiers?.length > 1) {
      const idx = response.tiers.findIndex(
        (tier) => tier.name.toLowerCase() === `pro`,
      );
      if (idx >= 0) {
        response.tiers[idx].badge = `Recommended`;
      }
    }

    return response;
  };

  return {
    getBillingInfo,
    changePricingTier,
    listTiers,
    upgradeToNewBilling,
    loading,
  };
}

export interface PricingInfoResponse {
  shopPricing: ShopPricing;
  appSubscription: AppSubscription;
  billingCycles: BillingCycleCharges[];
  estimatedOrders: number;
}

export interface BillingCycleCharges {
  id: string;
  startsAt: Date;
  endsAt: Date;
  createdAt: Date;
  externalId: string;
  pricingTier: string;
  openingOrdersBalance: number;
  closingOrdersBalance: number;
  openingBalance: number;
  closingBalance: number;
  totalPerCycle: number;
  charges: BillingCharge[];
}

export interface BillingCharge {
  why: string;
  amount: number;
}

export interface PublicBillingTierResp {
  tiers: PublicBillingTier[];
}

interface ShopPricing {
  createdAt: Date;
  tiersV2: V2PricingTier[];
  tierType: string;
  alias: string;
  minPricingTier?: string;
  ordersCountThisMonth?: number;
  trialDays?: number;
}

interface AppSubscription {
  alias: string;
  id: string;
  name: string;
  shopId: string;
  dollarsPerOrder: number;
  trialDaysUsed: number;
  isTestStore: boolean;
  currency: string;
  timeZone: string;
}

export interface PublicBillingTier {
  name: string;
  description: string;
  minOrders: number;
  maxOrders: number;
  pricePerMonth: number;
  annualPricePerMonth: number;
  beforeDiscountPrice: number;
  showRoi: boolean;
  roi: number;
  extendedTrial: boolean;
  perksLine: string;
  perks: string[];
  badge: string;
  perkHighlights: PerkHighlight[];
  reviewHighlights: PerkHighlight[];
  discounts: string[];
  pricingTier: V2PricingTier;
}

export interface PerkHighlight {
  icon: string;
  caption: string;
  tooltip: string;
}

export interface V2PricingTier {
  fixedPrice: number;
  id: string;
  includedOrders: string;
  name: string;
  tier: string;
}
